/* GProject scenario rail and shared detail dialog.
 * Original detail nodes move into the dialog and return on close.
 * Collection context reads public case relation attributes for visibility only.
 * Public case poster geometry and authorization remain outside this runtime.
 */
(function (window, document) {
  'use strict';

  function bindCollectionContext(root) {
    if (root.dataset.gprojectCollectionReady === 'true') return;

    const library = root.querySelector('.b2s-case-library');
    const notice = library?.querySelector('[data-gproject-collection-context]');
    const countLabel = notice?.querySelector('[data-gproject-collection-count]');
    const emptyLabel = notice?.querySelector('[data-gproject-collection-empty]');
    const clearLink = notice?.querySelector('[data-gproject-clear-collection]');
    const cards = Array.from(
      library?.querySelectorAll('[data-case-id][data-related-collections]') || []
    );
    if (!library || !notice || !countLabel || !emptyLabel || !clearLink) return;

    const allowedCollections = new Set([
      'infrastructure-engineering',
      'land-natural-resources',
      'urban-renewal',
      'property-assets-investment',
      'digital-city',
      'green-city-service'
    ]);
    const controller = new AbortController();
    let collectionHandle = '';

    function update() {
      const category = library.querySelector(
        '.b2s-case-filter__input:checked'
      )?.value || 'all';
      let count = 0;

      cards.forEach((card) => {
        const relatedHandles = (card.dataset.relatedCollections || '')
          .split('|')
          .map((handle) => handle.trim())
          .filter(Boolean);
        const matchesCollection =
          !collectionHandle || relatedHandles.includes(collectionHandle);

        if (matchesCollection) {
          card.removeAttribute('data-gproject-collection-hidden');
        } else {
          card.dataset.gprojectCollectionHidden = 'true';
        }

        if (
          matchesCollection &&
          (category === 'all' || card.dataset.caseCategory === category)
        ) count += 1;
      });

      notice.hidden = !collectionHandle;
      countLabel.textContent = (countLabel.dataset.countLabel || '%count%')
        .replace('%count%', String(count));
      emptyLabel.hidden = count > 0;
    }

    function readContext() {
      const url = new URL(window.location.href);
      const requested = (url.searchParams.get('collection') || '').trim();
      collectionHandle = allowedCollections.has(requested) ? requested : '';

      if (collectionHandle) {
        const allRadio = library.querySelector(
          '.b2s-case-filter__input[value="all"]'
        );
        if (allRadio) allRadio.checked = true;
      }

      url.searchParams.delete('collection');
      url.hash = 'showcase-projects';
      clearLink.href = url.pathname + url.search + url.hash;
      update();
    }

    readContext();

    library.addEventListener('change', (event) => {
      if (!event.target.matches('.b2s-case-filter__input')) return;

      update();

      const caseRail = library.querySelector('.b2s-project-grid--more');
      if (caseRail) caseRail.scrollLeft = 0;
    }, { signal: controller.signal });

    window.addEventListener('popstate', readContext, {
      signal: controller.signal
    });

    document.addEventListener('shopify:section:unload', (event) => {
      if (event.target === root || event.target.contains(root)) {
        controller.abort();
        cards.forEach((card) => {
          card.removeAttribute('data-gproject-collection-hidden');
        });
        notice.hidden = true;
        delete root.dataset.gprojectCollectionReady;
      }
    }, { signal: controller.signal });

    root.dataset.gprojectCollectionReady = 'true';
  }

  function mount(explorer) {
    if (explorer.dataset.glgScenarioReady === 'true') return;
    var rail = explorer.querySelector('[data-glg-scenario-rail]');
    var cards = rail ? Array.prototype.slice.call(rail.querySelectorAll('[data-brochure-scenario]')) : [];
    var status = explorer.querySelector('[data-glg-scenario-status]');
    var counter = explorer.querySelector('[data-glg-scenario-counter]');
    var previous = explorer.querySelector('[data-glg-scenario-prev]');
    var next = explorer.querySelector('[data-glg-scenario-next]');
    if (!rail || cards.length !== 8 || !status || !counter || !previous || !next) return;

    const controller = new AbortController();
    const dialog = explorer.querySelector('[data-glg-scenario-dialog]');
    const dialogTitle = dialog?.querySelector('[data-glg-scenario-dialog-title]');
    const dialogContent = dialog?.querySelector('[data-glg-scenario-dialog-content]');
    const dialogReading = dialog?.querySelector('[data-glg-scenario-dialog-reading]');
    const dialogImage = dialog?.querySelector('[data-glg-scenario-dialog-image]');
    const dialogClose = dialog?.querySelector('[data-glg-scenario-dialog-close]');
    const dialogFooter = dialog?.querySelector('[data-glg-scenario-dialog-footer]');
    const posterTriggers = [];
    const caseBindings = [];
    let movedContent = [];
    let activePosterTrigger = null;

    function restoreContent(restoreFocus = true) {
      movedContent.forEach(({ node, marker, detailsStates }) => {
        detailsStates.forEach(({ details, wasOpen }) => {
          details.open = wasOpen;
        });
        marker.replaceWith(node);
      });
      movedContent = [];
      if (dialog) delete dialog.dataset.gprojectDetailType;
      if (dialogFooter) delete dialogFooter.dataset.gprojectEmpty;
      if (dialogTitle) dialogTitle.textContent = '';
      if (dialogImage) {
        dialogImage.hidden = true;
        dialogImage.removeAttribute('src');
        dialogImage.alt = '';
      }
      document.documentElement.classList.remove('glg-scenario-dialog-open');
      const trigger = activePosterTrigger;
      activePosterTrigger = null;
      if (restoreFocus && trigger?.isConnected) {
        const focusTarget = trigger.getClientRects().length > 0
          ? trigger
          : trigger.closest('.b2s-project-grid--more') || rail;

        if (focusTarget?.isConnected) {
          focusTarget.focus({ preventScroll: true });
        }
      }
    }

    function openDetail(card, trigger, title, isPublicCase = false) {
      if (
        controller.signal.aborted ||
        !dialog?.isConnected ||
        typeof dialog.showModal !== 'function' ||
        !card?.isConnected ||
        !trigger?.isConnected ||
        dialog.open ||
        movedContent.length > 0
      ) return;

      const body = card.querySelector('.b2s-project-card__body');
      const heading = body?.querySelector(':scope > h3');
      if (!body || !heading) return;

      dialog.dataset.gprojectDetailType = isPublicCase ? 'case' : 'scenario';
      dialogTitle.textContent = title;

      const image = card.querySelector(
        isPublicCase
          ? '.b2s-project-card__visual img'
          : '.b2s-scenario-media img'
      );

      dialogImage.hidden = !image;
      if (image) {
        dialogImage.src = image.currentSrc || image.src;
        dialogImage.alt = image.alt;
      } else {
        dialogImage.removeAttribute('src');
        dialogImage.alt = '';
      }

      activePosterTrigger = trigger;

      try {
        movedContent = [];

        Array.from(body.children)
          .filter((node) =>
            node !== heading &&
            node !== trigger &&
            !node.classList.contains('b2s-project-card__topline') &&
            !(
              node.matches('.b2s-project-card__links') &&
              !node.querySelector('a[href]')
            )
          )
          .forEach((node) => {
            const marker = document.createComment('shared-detail-home');
            const detailsStates = [
              ...(node.matches('details') ? [node] : []),
              ...node.querySelectorAll('details')
            ].map((details) => ({
              details,
              wasOpen: details.open
            }));

            node.before(marker);
            movedContent.push({ node, marker, detailsStates });

            const destination = node.matches('.b2s-project-card__links')
              ? dialogFooter
              : dialogContent;

            destination.append(node);
            detailsStates.forEach(({ details }) => {
              details.open = true;
            });
          });

        dialogFooter.dataset.gprojectEmpty =
          dialogFooter.querySelector('a[href]') ? 'false' : 'true';

        dialog.showModal();

        const dialogHeader = dialog.querySelector('.b2s-scenario-dialog__header');
        if (dialogHeader) {
          dialogHeader.scrollTop = 0;
          dialogHeader.scrollLeft = 0;
        }

        dialogReading.scrollTop = 0;
        dialogReading.scrollLeft = 0;
        dialogFooter.scrollTop = 0;
        dialogFooter.scrollLeft = 0;

        document.documentElement.classList.add('glg-scenario-dialog-open');
        dialogClose.focus({ preventScroll: true });
      } catch (error) {
        if (dialog.open) dialog.close();

        if (isPublicCase) {
          delete card.dataset.gprojectCaseDialogReady;
          trigger.hidden = true;
        }

        restoreContent();

        console.error('GProject shared detail dialog could not open:', error);
      }
    }

    const canMountPosters =
      dialog &&
      typeof dialog.showModal === 'function' &&
      dialogTitle && dialogContent && dialogReading &&
      dialogImage && dialogClose && dialogFooter &&
      cards.every((card) =>
        card.querySelector('.b2s-project-card__body > h3')
      );

    if (canMountPosters) {
      dialogClose.addEventListener('click', () => dialog.close(), {
        signal: controller.signal
      });

      dialog.addEventListener('close', () => restoreContent(), {
        signal: controller.signal
      });

      dialog.addEventListener('click', (event) => {
        if (event.target !== dialog) return;
        const rect = dialog.getBoundingClientRect();
        if (
          event.clientX < rect.left || event.clientX > rect.right ||
          event.clientY < rect.top || event.clientY > rect.bottom
        ) dialog.close();
      }, { signal: controller.signal });

      cards.forEach((card) => {
        const heading = card.querySelector('.b2s-project-card__body > h3');
        const title = heading.textContent.trim();
        const trigger = document.createElement('button');
        trigger.type = 'button';
        trigger.className = 'b2s-scenario-poster-trigger';
        trigger.textContent = title;
        trigger.setAttribute('aria-haspopup', 'dialog');
        if (!dialog.id) dialog.id = `${cards[0].id}-detail-dialog`;
        trigger.setAttribute('aria-controls', dialog.id);
        heading.replaceChildren(trigger);
        posterTriggers.push(trigger);

        trigger.addEventListener('click', () => {
          openDetail(card, trigger, title);
        }, { signal: controller.signal });
      });

      const root = explorer.closest('[data-b2s-root]');

      root?.querySelectorAll('.b2s-case-library [data-case-id]')
        .forEach((card) => {
          if (card.dataset.gprojectCaseDialogReady === 'true') return;

          const heading = card.querySelector('.b2s-project-card__body > h3');
          const trigger = card.querySelector('[data-gproject-case-trigger]');
          if (!heading || !trigger) return;

          const title = heading.textContent.trim();
          if (!title) return;

          trigger.setAttribute('aria-controls', dialog.id);
          trigger.addEventListener('click', () => {
            openDetail(card, trigger, title, true);
          }, { signal: controller.signal });

          caseBindings.push({ card, trigger });
          card.dataset.gprojectCaseDialogReady = 'true';
          trigger.hidden = false;
        });

      explorer.dataset.glgPosterReady = 'true';
    }

    var frame = 0;
    var active = 0;
    var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function update() {
      frame = 0;
      if (controller.signal.aborted) return;
      var maxScroll = Math.max(0, rail.scrollWidth - rail.clientWidth);
      // Tablet cards cannot always align to the left at the physical rail end.
      if (maxScroll > 0 && rail.scrollLeft >= maxScroll - 2) {
        active = cards.length - 1;
      } else {
        var railLeft = rail.getBoundingClientRect().left;
        var distance = Infinity;
        cards.forEach(function (card, index) {
          var gap = Math.abs(card.getBoundingClientRect().left - railLeft);
          if (gap < distance) {
            distance = gap;
            active = index;
          }
        });
      }
      counter.textContent = String(active + 1).padStart(2, '0') + ' / 08';
      previous.disabled = active === 0;
      next.disabled = active === cards.length - 1;
    }

    function queueUpdate() {
      if (controller.signal.aborted || frame) return;
      frame = window.requestAnimationFrame(update);
    }

    function move(delta) {
      var target = cards[Math.max(0, Math.min(cards.length - 1, active + delta))];
      if (!target) return;
      var requestedLeft = Math.max(0, target.offsetLeft - cards[0].offsetLeft);
      var maxScroll = Math.max(0, rail.scrollWidth - rail.clientWidth);
      // Previous must escape the end even when its target cannot align left.
      if (delta < 0 && active === cards.length - 1 && requestedLeft >= maxScroll && maxScroll > 0) {
        requestedLeft = Math.max(0, maxScroll - Math.max(12, Math.min(64, rail.clientWidth * 0.1)));
      }
      rail.scrollTo({
        left: requestedLeft,
        behavior: reducedMotion.matches ? 'auto' : 'smooth'
      });
    }

    function syncHash() {
      var hash = window.location && window.location.hash ? window.location.hash.slice(1) : '';
      if (!hash) return;
      try { hash = decodeURIComponent(hash); } catch (_error) { return; }
      var index = cards.findIndex(function (card) { return card.id === hash; });
      if (index < 0) return;
      if (typeof rail.scrollTo === 'function') {
        rail.scrollTo({ left: Math.max(0, cards[index].offsetLeft - cards[0].offsetLeft), behavior: 'auto' });
      }
      update();
      if (typeof cards[index].scrollIntoView === 'function') {
        cards[index].scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'nearest' });
      }
    }

    function cleanup() {
      if (controller.signal.aborted) return;
      controller.abort();

      if (frame) window.cancelAnimationFrame(frame);
      frame = 0;

      if (dialog?.open) dialog.close();
      restoreContent(false);

      caseBindings.forEach(({ card, trigger }) => {
        delete card.dataset.gprojectCaseDialogReady;
        trigger.hidden = true;
        trigger.removeAttribute('aria-controls');
      });

      posterTriggers.forEach((trigger) => {
        trigger.replaceWith(document.createTextNode(trigger.textContent));
      });

      delete explorer.dataset.glgPosterReady;
      status.hidden = true;
      delete explorer.dataset.glgScenarioReady;
    }

    rail.addEventListener('scroll', queueUpdate, { passive: true, signal: controller.signal });
    window.addEventListener('resize', queueUpdate, { passive: true, signal: controller.signal });
    window.addEventListener('pageshow', queueUpdate, { passive: true, signal: controller.signal });
    window.addEventListener('hashchange', syncHash, { signal: controller.signal });
    previous.addEventListener('click', function () { move(-1); }, { signal: controller.signal });
    next.addEventListener('click', function () { move(1); }, { signal: controller.signal });
    document.addEventListener('shopify:section:unload', function (event) {
      if (event.target === explorer || event.target.contains(explorer)) cleanup();
    }, { signal: controller.signal });

    update();
    syncHash();
    explorer.dataset.glgScenarioReady = 'true';
    status.hidden = false;
  }

  function mountAll(scope) {
    if (!scope) return;

    const owners = new Set();
    const owner = scope.matches?.('[data-b2s-root]')
      ? scope
      : scope.closest?.('[data-b2s-root]');

    if (owner) owners.add(owner);
    scope.querySelectorAll?.('[data-b2s-root]').forEach((root) => {
      owners.add(root);
    });

    owners.forEach((root) => {
      try {
        bindCollectionContext(root);
      } catch (error) {
        console.error('GProject collection context initialization failed:', error);
      }
    });

    if (scope.matches?.('[data-glg-scenario-explorer]')) mount(scope);
    scope.querySelectorAll?.('[data-glg-scenario-explorer]').forEach(mount);
  }

  const previousBoot = window.__GLG_BROCHURE_SCENARIO_BOOT__;
  if (previousBoot) {
    document.removeEventListener('shopify:section:load', previousBoot);
    document.removeEventListener('DOMContentLoaded', previousBoot);
  }

  const boot = function (event) {
    const scope = event && event.type === 'shopify:section:load'
      ? event.target
      : document;
    mountAll(scope);
  };

  window.__GLG_BROCHURE_SCENARIO_BOOT__ = boot;
  document.addEventListener('shopify:section:load', boot);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})(window, document);
